import { computed, type ComputedRef } from 'vue';
import { useStore } from 'vuex';
import { camelcaseFilterParamsKeys } from '@/composable/user/leadList/lead-filter';
import { key } from '@/store/index';
import type { RootState } from '@/store/types';

interface UseLeadViewConditionDependencies {
  defaultDisplayColumns: ComputedRef<string[]>;
}

export const useLeadViewCondition = (dependencies: UseLeadViewConditionDependencies) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const store = useStore<RootState>(key);

  const leadView = computed(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
    store.getters['user/leadView'],
  );

  const leadListSortKey = computed(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
    store.getters['userUi/leadListSortKey'],
  );

  const leadListSortOrder = computed(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
    store.getters['userUi/leadListSortOrder'],
  );

  const leadListSelectedColumns = computed(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
    store.getters['userUi/leadListSelectedColumns'],
  );

  const leadListFilterParams = computed(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
    store.getters['userUi/leadListFilterParams'],
  );

  const filterParams = computed(() => {
    if (leadView.value?.filterParams) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return camelcaseFilterParamsKeys(JSON.parse(leadView.value.filterParams));
    }
    return {};
  });

  const displayColumns = computed(() => {
    if (leadView.value?.displayColumns) {
      return JSON.parse(leadView.value.displayColumns);
    }
    return dependencies.defaultDisplayColumns.value || [];
  });

  const sortOrder = computed(() => leadView.value?.sortOrder || 'desc');

  const sortKey = computed(() => leadView.value?.sortKey || 'callTargetCreatedAt');

  const isFilterParamsChanged = computed(() =>
    JSON.stringify(filterParams.value) !== JSON.stringify(leadListFilterParams.value),
  );

  const isSorterdColumnsChanged = computed(() =>
    sortOrder.value !== leadListSortOrder.value || sortKey.value !== leadListSortKey.value,
  );

  const isDisplayColumnsChanged = computed(() =>
    displayColumns.value.toString() !== leadListSelectedColumns.value.toString(),
  );

  const isConditionChanged = computed(() =>
    isFilterParamsChanged.value || isSorterdColumnsChanged.value || isDisplayColumnsChanged.value,
  );

  return {
    displayColumns,
    isConditionChanged,
  };
};
